const REPRINT_LABEL_BASE = 'https://rmafunctionsv2.azurewebsites.net/api/ReprintLabel?file='

export const GENERIC_ERROR_MESSAGE =
  'Something went wrong. Please try again or contact support@braceability.com.'

export const GENERIC_EMAIL_ERROR_MESSAGE =
  'We could not send your email. Please try again or contact support@braceability.com.'

export interface NormalizedBuildReturnResult {
  returnOrderNumber: string
  returnOrderId: number
  zip: string
  shippingInstructionsFileName: string
  isReship: boolean
  errors: unknown[]
  [key: string]: unknown
}

export interface EmailShipInstructionsPayload {
  CustomerEmail: string
  OrderNumber: string
  EmailBody: string
  ShippingInstructionsLink: string
  OrderId: number
}

function asRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return { ...(value as Record<string, unknown>) }
  }
  return {}
}

function asString(value: unknown): string {
  if (value === null || value === undefined) {
    return ''
  }
  return String(value).trim()
}

function asNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export function parseStoredJson<T>(storageKey: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) {
      return fallback
    }
    const parsed: unknown = JSON.parse(raw)
    if (parsed === null || typeof parsed !== 'object') {
      return fallback
    }
    return parsed as unknown as T
  } catch {
    return fallback
  }
}

export function normalizeBuildReturnResult(
  buildReturnResult: unknown,
  orderInfo: unknown
): NormalizedBuildReturnResult {
  const results = asRecord(buildReturnResult)
  const order = asRecord(orderInfo)
  const reference = asString(
    results.returnOrderNumber ?? order.reference ?? order.originReference
  )
  const zip = asString(results.zip ?? order.zip)
  const returnOrderId = asNumber(
    results.returnOrderId ?? order.rootOriginOrderId ?? order.originOrderId,
    0
  )
  const fileName = asString(
    results.shippingInstructionsFileName ??
    results.shipInstructionsFileName ??
    results.shippingInstructionFileName
  )

  return {
    ...results,
    returnOrderNumber: reference || 'Return',
    returnOrderId,
    zip,
    shippingInstructionsFileName: fileName,
    isReship: Boolean(results.isReship),
    errors: Array.isArray(results.errors) ? results.errors : [],
  }
}

export function buildInstructionsUrl(fileName: unknown): string {
  const name = asString(fileName)
  if (!name) {
    return ''
  }
  return REPRINT_LABEL_BASE + encodeURIComponent(name)
}

export function buildStatusPageUrl(orderId: unknown, postalCode: unknown): string {
  return `https://www.braceability.com/pages/returns?orderId=${encodeURIComponent(asString(orderId))}&postalCode=${encodeURIComponent(asString(postalCode))}`
}

export function normalizeEmail(email: unknown): string {
  return asString(email).toLowerCase()
}

export function isValidEmail(email: unknown): boolean {
  const normalized = normalizeEmail(email)
  return normalized.length >= 6 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)
}

export function buildEmailShipInstructionsPayload(
  emailAddress: unknown,
  emailBody: unknown,
  buildReturnResult: unknown,
  orderInfo: unknown
): EmailShipInstructionsPayload | null {
  const customerEmail = normalizeEmail(emailAddress)
  if (!isValidEmail(customerEmail)) {
    return null
  }

  const order = asRecord(orderInfo)
  const results = normalizeBuildReturnResult(buildReturnResult, order)
  const reference = asString(order.reference ?? results.returnOrderNumber)
  const postalCode = asString(results.zip ?? order.zip)
  const instructionsUrl = buildInstructionsUrl(results.shippingInstructionsFileName)
  const statusLink = buildStatusPageUrl(reference || results.returnOrderNumber, postalCode)
  const orderNumber = asString(results.returnOrderNumber) || reference || 'Return'
  const body = typeof emailBody === 'string' ? emailBody : ''

  if (!body) {
    return null
  }

  return {
    CustomerEmail: customerEmail,
    OrderNumber: orderNumber,
    EmailBody: body,
    ShippingInstructionsLink: instructionsUrl || statusLink,
    OrderId: results.returnOrderId,
  }
}

export function getPrepaidStepImageBase(orderInfo: unknown): string {
  const order = asRecord(orderInfo)
  let baseStepUrl = 'https://babotstorage.blob.core.windows.net/public/'
  const summary = asRecord(order.summary)
  const skipShipping = summary.skipShipping === true || order.useOwnLabel === true
  if (order.reshipOrder === true || !skipShipping) {
    baseStepUrl += 'pre-pay-'
  }
  return baseStepUrl
}
